import { useRef, useState, useEffect } from "react";
import { Upload, Download, Trash2, Loader2, FileText, Image, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import {
  ALLOWED_PERSONAL_DOCUMENT_TYPES,
  MAX_PERSONAL_DOCUMENT_SIZE_BYTES,
  MAX_PERSONAL_DOCUMENT_SIZE_MB,
  MAX_TOTAL_PERSONAL_SPACE_MB,
  MAX_TOTAL_PERSONAL_SPACE_BYTES,
  validateFileSize,
  validateFileType,
  formatFileSize,
} from "../../config/fileConfig";

function getAuthHeaders() {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getFileIcon(mimeType) {
  if (mimeType && mimeType.startsWith("image/")) return <Image size={16} className="text-blue-500" />;
  return <FileText size={16} className="text-red-500" />;
}

function formatMimeLabel(mimeType) {
  if (!mimeType) return "—";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType === "application/msword") return "DOC";
  if (mimeType.includes("wordprocessingml")) return "DOCX";
  if (mimeType.startsWith("image/")) return mimeType.split("/")[1].toUpperCase();
  return mimeType.split("/")[1]?.toUpperCase() ?? mimeType;
}

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function MainPersonalSpace() {
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState([]); // [{ name, progress, status }]
  const [deleteTarget, setDeleteTarget] = useState(null); // doc object to confirm
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // ── Fetch personal documents on mount ──────────────────────────────────────
  useEffect(() => {
    fetchDocuments();
  }, []);

  async function fetchDocuments() {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_USER_DOCUMENTS}?documentType=PERSONAL_DOCUMENT`,
        { headers: { ...getAuthHeaders() } }
      );
      const result = await res.json();
      if (result.statusCode === 200 && Array.isArray(result.data)) {
        setDocuments(result.data);
      } else {
        setDocuments([]);
      }
    } catch {
      toast.error("Failed to load documents.");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }

  // ── Compute total used space ────────────────────────────────────────────────
  const totalUsedBytes = documents.reduce((sum, d) => sum + (d.fileSize || 0), 0);
  const usagePercent = Math.min((totalUsedBytes / MAX_TOTAL_PERSONAL_SPACE_BYTES) * 100, 100);
  const isStorageFull = totalUsedBytes >= MAX_TOTAL_PERSONAL_SPACE_BYTES;

  // ── Handle file selection & upload ─────────────────────────────────────────
  function handleFileChange(e) {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;

    const validFiles = [];
    for (const file of files) {
      const typeCheck = validateFileType(file, ALLOWED_PERSONAL_DOCUMENT_TYPES);
      if (!typeCheck.valid) { toast.error(`${file.name}: ${typeCheck.error}`); continue; }
      const sizeCheck = validateFileSize(file, MAX_PERSONAL_DOCUMENT_SIZE_BYTES);
      if (!sizeCheck.valid) { toast.error(`${file.name}: ${sizeCheck.error}`); continue; }
      validFiles.push(file);
    }

    if (!validFiles.length) return;

    // Check total space
    const newFilesBytes = validFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalUsedBytes + newFilesBytes > MAX_TOTAL_PERSONAL_SPACE_BYTES) {
      toast.error(
        `Not enough space. You have ${formatFileSize(MAX_TOTAL_PERSONAL_SPACE_BYTES - totalUsedBytes)} remaining of your ${MAX_TOTAL_PERSONAL_SPACE_MB}MB limit.`
      );
      return;
    }

    uploadFiles(validFiles);
  }

  async function uploadFiles(files) {
    // Initialize queue entries
    const entries = files.map((f) => ({ name: f.name, progress: 0, status: "uploading" }));
    setUploadQueue(entries);

    let anySuccess = false;
    for (let i = 0; i < files.length; i++) {
      // eslint-disable-next-line no-loop-func
      await new Promise((resolve) => {
        const formData = new FormData();
        formData.append("file", files[i]);
        formData.append("documentType", "PERSONAL_DOCUMENT");

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE_URL}${API_ENDPOINTS.UPLOAD_USER_DOCUMENT}`);
        const headers = getAuthHeaders();
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setUploadQueue((prev) =>
              prev.map((e, idx) => (idx === i ? { ...e, progress: pct } : e))
            );
          }
        };

        xhr.onload = () => {
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.statusCode === 200 || result.statusCode === 201) {
              setUploadQueue((prev) =>
                prev.map((e, idx) => (idx === i ? { ...e, progress: 100, status: "done" } : e))
              );
              anySuccess = true;
            } else {
              setUploadQueue((prev) =>
                prev.map((e, idx) => (idx === i ? { ...e, status: "error" } : e))
              );
              toast.error(`${files[i].name}: ${result.message || "Upload failed."}`);
            }
          } catch {
            setUploadQueue((prev) =>
              prev.map((e, idx) => (idx === i ? { ...e, status: "error" } : e))
            );
          }
          resolve();
        };

        xhr.onerror = () => {
          setUploadQueue((prev) =>
            prev.map((e, idx) => (idx === i ? { ...e, status: "error" } : e))
          );
          toast.error(`${files[i].name}: Upload failed.`);
          resolve();
        };

        xhr.send(formData);
      });
    }

    if (anySuccess) {
      toast.success("Upload complete.");
      await fetchDocuments();
    }
    // Clear queue after a short delay
    setTimeout(() => setUploadQueue([]), 2000);
  }

  // ── Delete document ─────────────────────────────────────────────────────────
  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.DELETE_USER_DOCUMENT}/${deleteTarget.id}`,
        {
          method: "DELETE",
          headers: { ...getAuthHeaders() },
        }
      );
      const result = await res.json();
      if (result.statusCode === 200) {
        toast.success("Document deleted.");
        setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
        setDeleteTarget(null);
      } else {
        toast.error(result.message || "Could not delete document.");
      }
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  // ── Toggle document visibility ──────────────────────────────────────────────
  async function handleToggleVisibility(doc) {
    setTogglingId(doc.id);
    try {
      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.UPDATE_DOCUMENT_VISIBILITY}/${doc.id}/visibility`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ isPublic: !doc.isPublic }),
        }
      );
      if (!res.ok) throw new Error();
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, isPublic: !doc.isPublic } : d))
      );
      toast.success(!doc.isPublic ? "Document is now public." : "Document is now private.");
    } catch {
      toast.error("Failed to update visibility. Please try again.");
    } finally {
      setTogglingId(null);
    }
  }

  // ── Download document ───────────────────────────────────────────────────────
  async function handleDownload(doc) {
    try {
      const res = await fetch(doc.documentLink);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please try again.");
    }
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-popinsSemiBold text-gray-800">Personal Space</h1>
          <p className="text-sm text-gray-500 font-popinsRegular mt-1">
            Upload, view, and manage your personal documents securely.
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadQueue.length > 0 || isStorageFull}
          className="flex items-center gap-2 bg-themeColor text-white px-5 py-2.5 rounded-lg text-sm font-popinsMedium hover:bg-opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploadQueue.length > 0 ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
          {uploadQueue.length > 0 ? "Uploading…" : "Upload Documents"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.jpeg,.jpg,.png,.webp,.gif"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Storage usage bar */}
      {!loading && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-popinsMedium text-gray-500">Storage Used</span>
            <span className={`text-xs font-popinsMedium ${isStorageFull ? "text-red-500" : "text-gray-500"}`}>
              {formatFileSize(totalUsedBytes)} / {MAX_TOTAL_PERSONAL_SPACE_MB} MB
              {isStorageFull && " — Storage full"}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${isStorageFull ? "bg-red-500" : usagePercent > 80 ? "bg-amber-400" : "bg-themeColor"}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>
      )}

      {/* File size note */}
      <p className="text-xs text-gray-400 font-popinsRegular mb-6">
        Supported formats: PDF, DOC, DOCX, JPEG, PNG, WebP, GIF &nbsp;·&nbsp; Max size per file: {MAX_PERSONAL_DOCUMENT_SIZE_MB}MB
      </p>

      {/* Upload Progress */}
      {uploadQueue.length > 0 && (
        <div className="mb-6 space-y-3">
          {uploadQueue.map((item, idx) => (
            <div key={idx} className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-popinsRegular text-gray-700 truncate max-w-[70%]">
                  {item.name}
                </span>
                <span className={`text-xs font-popinsMedium ${item.status === "error" ? "text-red-500" : item.status === "done" ? "text-green-500" : "text-themeColor"}`}>
                  {item.status === "error" ? "Failed" : item.status === "done" ? "Done" : `${item.progress}%`}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all duration-200 ${item.status === "error" ? "bg-red-400" : "bg-themeColor"}`}
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document List */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 size={32} className="animate-spin text-themeColor" />
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <FileText size={48} className="mb-4 opacity-40" />
          <p className="text-base font-popinsMedium">No documents yet</p>
          <p className="text-sm font-popinsRegular mt-1">Click "Upload Document" to add your first file.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    File Name
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Type
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Size
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Uploaded
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Visibility
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getFileIcon(doc.mimeType)}
                        <span className="font-popinsRegular text-gray-800 truncate max-w-[220px]">
                          {doc.filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block bg-gray-100 text-gray-600 text-xs font-popinsMedium px-2 py-0.5 rounded">
                        {formatMimeLabel(doc.mimeType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular">
                      {doc.fileSize ? formatFileSize(doc.fileSize) : "—"}
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular">
                      {formatDate(doc.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleVisibility(doc)}
                        disabled={togglingId === doc.id}
                        title={doc.isPublic ? "Public — click to make private" : "Private — click to make public"}
                        className="flex items-center gap-1.5 text-xs font-popinsMedium transition-colors disabled:opacity-40"
                      >
                        {togglingId === doc.id ? (
                          <Loader2 size={14} className="animate-spin text-gray-400" />
                        ) : doc.isPublic ? (
                          <>
                            <Eye size={14} className="text-themeColor" />
                            <span className="text-themeColor">Public</span>
                          </>
                        ) : (
                          <>
                            <EyeOff size={14} className="text-gray-400" />
                            <span className="text-gray-400">Private</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleDownload(doc)}
                          title="Download"
                          className="text-gray-400 hover:text-themeColor transition-colors"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(doc)}
                          title="Delete"
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto mb-4">
                <Trash2 size={20} className="text-red-500" />
              </div>
              <h2 className="text-base font-popinsSemiBold text-gray-800 text-center">
                Delete Document
              </h2>
              <p className="text-sm font-popinsRegular text-gray-500 text-center mt-2">
                Are you sure you want to delete{" "}
                <span className="font-popinsMedium text-gray-700">
                  {deleteTarget.filename}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-popinsMedium text-gray-600 border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-popinsMedium bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
